import type { CSSProperties, RefObject } from 'react'

import { useEffect, useRef } from 'react'

/**
 * Reveals every `[data-reveal]` descendant as it enters view, once. Pair with
 * the `rev-reveal` utility; stagger with `revealDelay()`.
 */
export const useRevealGroup = <T extends HTMLElement>(): RefObject<T | null> => {
    const ref = useRef<T | null>(null)

    useEffect(() => {
        const root = ref.current
        if (!root) return

        const items = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'))

        const io = new IntersectionObserver(
            entries => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue

                    entry.target.classList.add('rev-shown')
                    io.unobserve(entry.target)
                }
            },
            { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
        )

        for (const el of items) io.observe(el)

        /**
         * Safety net. These pages are entirely reveal-driven, so anything the
         * observer fails to report would stay invisible for good. Shortly after
         * mount, show whatever is already on screen regardless.
         */
        const sweep = window.setTimeout(() => {
            for (const el of items) {
                if (el.classList.contains('rev-shown')) continue

                const box = el.getBoundingClientRect()

                if (box.top < window.innerHeight && box.bottom > 0) {
                    el.classList.add('rev-shown')
                    io.unobserve(el)
                }
            }
        }, 1200)

        return (): void => {
            window.clearTimeout(sweep)
            io.disconnect()
        }
    }, [])

    return ref
}

export const revealDelay = (ms: number): CSSProperties =>
    ({ '--rev-delay': `${ms}ms` }) as CSSProperties

/**
 * Drifts every `[data-drift]` element against the scroll, at the rate given by
 * its `data-drift` value. One rAF pass, transforms only.
 */
export const useDrift = <T extends HTMLElement>(): RefObject<T | null> => {
    const ref = useRef<T | null>(null)

    useEffect(() => {
        const root = ref.current
        if (!root) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const items = Array.from(root.querySelectorAll<HTMLElement>('[data-drift]')).map(el => ({
            el,
            rate: Number(el.dataset.drift ?? 0)
        }))

        if (!items.length) return

        let raf = 0
        let queued = false

        const write = (): void => {
            queued = false

            const mid = window.innerHeight / 2

            for (const item of items) {
                const box = item.el.getBoundingClientRect()
                const from = box.top + box.height / 2 - mid

                item.el.style.transform = `translate3d(0, ${(-from * item.rate).toFixed(2)}px, 0)`
            }
        }

        const onScroll = (): void => {
            if (queued) return
            queued = true
            raf = requestAnimationFrame(write)
        }

        write()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return (): void => {
            cancelAnimationFrame(raf)
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
        }
    }, [])

    return ref
}
